import * as Dynamo from '../DynamoTypes';
import { AvatarChange } from './sharedTypes';
import * as MedicalDataTypes from '../MedicalDataTypes';
import { APITypes } from '..';
export type NoteCommentMetadata = {
    createdBy: Dynamo.User | null;
};

export type NoteCommentWithMetadata = Dynamo.WithTimestamps<Dynamo.NoteComment> & NoteCommentMetadata;

/**
 * Where a note's underlying record came from, resolved from the note's resource at read time rather than stored
 * on the note. `id` is the stable key to group and filter by — the Fasten org connection the record was imported
 * through. Pre-Fasten 1upHealth records have no such id, so they fall back to grouping by `label`, which is the
 * health system's display name as the portal reported it.
 */
export type NoteOriginSource = {
    type: 'health-system';
    id: string | null;
    label: string | null;
};

/**
 * Read-time state of an organization-client-record note that is a copy of a profile note. Liveness is computed on
 * every read and never stored: a copy is live while its source note still exists, the organization can view it under
 * the profile's normal collaborator rules, and nobody has edited the copy by hand. A live copy tracks its source and
 * is not editable; a copy that is no longer live (the organization was disconnected, lost access, the source was
 * deleted, or the copy was edited) is retained as an ordinary editable client-record note and never becomes live
 * again once edited.
 */
export type NoteMirrorMetadata = {
    isLive: boolean;
    sourceNoteId: string;
    sourceMedicalProfileId: string | null;
    /** The source note's entity type, needed to navigate to it. Null once the source note no longer exists. */
    sourceNoteEntityType: Dynamo.NoteEntityType | null;
    /**
     * Whether the requesting user can open the source note. True only while the source exists and the organization
     * can still view it, which includes a retained copy that was edited by hand while the organization is connected.
     */
    canOpenSource: boolean;
};

/** An organization client record holding a copy of a profile note. */
export type NoteMirroredToTarget = {
    organizationId: string;
    organizationClientRecordId: string;
    /** The organization-client-record note holding the copy. */
    mirrorNoteId: string;
};

export type NoteMetadata = {
    resourceType: MedicalDataTypes.PrimaryRecordResourceCategory | null;
    /**
     * The health system a portal-imported note originated from, or null for notes people wrote themselves.
     * Drives the notes Source filter and the note's creator byline.
     */
    originSource: NoteOriginSource | null;
    createdBy: Dynamo.User | null;
    /**
     * When set, the app displays this string as "Created By" instead of resolving createUserId to a user —
     * e.g. the physician or source portal for a system-generated clinical note.
     */
    createdBySourceLabel: string | null;
    lastModifiedBy: Dynamo.User | null;
    comments: NoteCommentWithMetadata[];
    /** Present only on an organization-client-record note that is a copy of a profile note. */
    mirror?: NoteMirrorMetadata;
    /**
     * The organization client records holding a copy of this profile note. Resolved only for single-note detail
     * reads of a profile note; absent on list reads and on every other note.
     */
    mirroredTo?: NoteMirroredToTarget[];
};

export type WithMappedContentFormats<T extends Dynamo.Note> = T & {
    plainTextContent: string;
    markdownContent: string;
    htmlContent: string;
};

export type NoteListItem = WithMappedContentFormats<Dynamo.WithTimestamps<Dynamo.Note> & NoteMetadata>;

export type NotesList = {
    notes: NoteListItem[];
};

export type NoteMedicalProfileFile = APITypes.medicalProfileFilesService.MedicalProfileFileListItem;

export type NoteDetails = NoteListItem & {
    medicalProfileFiles: NoteMedicalProfileFile[];
};

export type NoteFileCreate = {
    type: 'create';
    tempFileId: string;
    description: string;
    name: string;
    contentType: string;
};

export type NoteFileUpdateExisting = {
    type: 'update';
    fileId: string;
    description: string;
    fileChange: AvatarChange;
    name: string;
    contentType: string;
};

export type NoteFileNotModified = {
    type: 'not-modified';
    fileId: string;
    description: string;
};

export type NoteFileUpdate = NoteFileUpdateExisting | NoteFileCreate | NoteFileNotModified;

export type NoteUpsert = Pick<
    Dynamo.Note,
    'title' | 'content' | 'tags' | 'entityType' | 'entityId' | 'medicalProfileId' | 'organizationId'
> & {
    images: NoteFileUpdate[];
    files: NoteFileUpdate[];
};

export type GetNotFileDownloadUrlResponse = {
    downloadUrl: string;
};

export type NoteCommentRequest = {
    comment: string;
};

export type GetResourceGroupNotesRequest = {
    resourceGroupIds: string[];
};

export type CopyToProfileRequest = {
    destinationProfileId: string;
};

export type CopyToProfileResponse = {
    note: WithMappedContentFormats<Dynamo.Note>;
};

export type CopyOrganizationNoteToMedicalProfilesRequest = {
    destinationMedicalProfileIds: string[];
};

export type CopyOrganizationNoteToMedicationProfileRequest = Pick<Dynamo.Note, 'entityType' | 'entityId'>;

export type SendNoteEmailRequest = {
    emailAddresses: string[];
};

/** Outcome of linking one profile note to the caller's organization client record. */
export type LinkNoteToClientRecordResult = 'linked' | 'already-linked';

export type LinkNoteToClientRecordResponse = {
    /** The organization-client-record note holding the copy. */
    mirrorNoteId: string;
    /** The client record the copy lives on. */
    organizationClientRecordId: string;
    result: LinkNoteToClientRecordResult;
};
